/**
 * Contacts-tab query hooks. Same shape as `use-inbox.ts`: key on the semantic filter set, 60 s
 * stale time, `keepPreviousData` only to tell a recount from a first load (use-filtered-query.ts).
 */

import { keepPreviousData } from '@tanstack/react-query';

import { useFilteredQuery } from '@/hooks/use-filtered-query';

import { filtersQuestion, type Filters } from '@/lib/filters-schema';
import { fetchContactsAttempts, fetchContactsKpis } from '@/services/contacts-api';
import type { ContactsAttemptsPage, ContactsKpis, ContactsSliceKind } from '@/services/api-types';

export function useContactsKpis(filters: Filters) {
  return useFilteredQuery<ContactsKpis>({
    queryKey: ['contacts', 'kpis', filtersQuestion(filters)],
    queryFn: async () => (await fetchContactsKpis(filters)).data,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** What a click on the tab opened: a slice kind and, for every kind but `all`, its value. */
export interface ContactsSlice {
  kind: ContactsSliceKind;
  value?: string;
}

/** The drill-down's page. Fires only while the modal is open, like `useTracesList`. */
export function useContactsAttempts(filters: Filters, slice: ContactsSlice, offset: number, enabled: boolean) {
  return useFilteredQuery<ContactsAttemptsPage>({
    queryKey: ['contacts', 'attempts', slice.kind, slice.value ?? null, offset, filtersQuestion(filters)],
    queryFn: async () => (await fetchContactsAttempts(filters, slice, offset, 25)).data,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    enabled,
  });
}
